import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import {
  ADD_STATIC_STRINGS,
  ADD_STATIC_STRINGS_JSON,
  GET_STATIC_STRINGS,
  UPDATE_STATIC_STRINGS,
  DELETE_STATIC_STRINGS,
} from "../../../api/url";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { Loader, LanguageTabs, EmptyState, Card, PageHeader } from "../../../components/ui";
import DeletModel from "../../../components/model/deleteModel";
import { toast } from "react-toastify";
import { validateKey, removeQuotes, getErrorMessage } from "./utils";
import AddButton from "./AddButton";
import StaticStringsTable from "./StaticStringsTable";
import StaticStringsModal from "./StaticStringsModal";
import { strings } from "../../../strings/strings";

const StaticStrings = () => {
  const queryClient = useQueryClient();

  const [activeLang, setActiveLang] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [modalData, setModalData] = useState({ id: null, key: "", value: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [addMode, setAddMode] = useState("single");
  const [keyError, setKeyError] = useState("");
  const [isOpeningModal, setIsOpeningModal] = useState(false);
  const [showAddOptionsHeader, setShowAddOptionsHeader] = useState(false);

  const {
    data,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
    // eslint-disable-next-line no-unused-vars
    refetch,
  } = useAppQuery({
    queryKey: [QUERY_KEYS.QUERY_KEY_STATIC_STRINGS, activeLang],
    url: `${GET_STATIC_STRINGS}?language=${activeLang}`,
    method: "GET",
    enabled: !!activeLang,
  });

  const { mutate, isPending: isMutating } = useAppMutation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeLang]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAddOptions || showAddOptionsHeader) {
        const target = event.target;
        const isInsideDropdown =
          target.closest(".add-options-container") || target.closest('[class*="absolute"]') || target.closest("button");
        if (!isInsideDropdown) {
          setShowAddOptions(false);
          setShowAddOptionsHeader(false);
        }
      }
    };
    if (showAddOptions || showAddOptionsHeader) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddOptions, showAddOptionsHeader]);

  const invalidateAndRefetch = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUERY_KEY_STATIC_STRINGS, activeLang] });
 
  };

  const resetModal = () => {
    setShowModal(false);
    setModalData({ id: null, key: "", value: "" });
    setKeyError("");
    setShowAddOptions(false);
    setShowAddOptionsHeader(false);
    setIsOpeningModal(false);
  };

  const handleUpdateClick = (item) => {
    setModalType("edit");
    const stringKey = removeQuotes(item.string_key);
    const stringValue = removeQuotes(item.value);

    setModalData({ id: item.id, key: stringKey, value: stringValue, originalValue: stringValue });
    setShowModal(true);
  };

  const handleOptionSelect = (mode, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (isOpeningModal) return;
    if (!activeLang) return toast.error(strings.pleaseSelectLanguage);

    setIsOpeningModal(true);
    setShowAddOptions(false);
    setShowAddOptionsHeader(false);

    setTimeout(() => {
      setAddMode(mode);
      setModalType("add");
      setModalData({ id: null, key: "", value: "" });
      setKeyError("");
      setShowModal(true);
      setIsOpeningModal(false);
    }, 0);
  };

  const handleAddSingle = () => {
    if (!activeLang) return toast.error(strings.pleaseSelectLanguage);

    const trimmedKey = modalData.key?.trim();
    const trimmedValue = modalData.value?.trim();

    if (!trimmedKey || !trimmedValue) return toast.error(strings.bothareRequired);

    const keyValidationError = validateKey(trimmedKey);
    if (keyValidationError) {
      setKeyError(keyValidationError);
      toast.error(keyValidationError);
      resetModal();
      return;
    }

    mutate(
      {
        url: ADD_STATIC_STRINGS,
        method: "POST",
        data: { string_key: trimmedKey, value: trimmedValue, language: activeLang.trim() },
      },
      {
        onSuccess: () => {
          toast.success("String added successfully");
          resetModal();
          invalidateAndRefetch();
        },
        onError: (err) => {
          toast.error(getErrorMessage(err));
          resetModal();
        },
      }
    );
  };

  const handleAddJSON = () => {
    if (!activeLang) return toast.error(strings.pleaseSelectLanguage);

    let parsedData;
    try {
      const input = modalData.value.trim();
      // eslint-disable-next-line no-new-func
      parsedData = Function('"use strict";return (' + input + ")")();
      if (typeof parsedData !== "object" || parsedData === null || Array.isArray(parsedData)) {
        throw new Error(strings.parseinputError);
      }
    } catch (err) {
      toast.error(strings.parseinputError);
      return;
    }

    mutate(
      { url: ADD_STATIC_STRINGS_JSON, method: "POST", data: { language: activeLang.trim(), strings: parsedData } },
      {
        onSuccess: (res) => {
          toast.success(res.message);
          resetModal();
          invalidateAndRefetch();
        },
        onError: (err) => {
          toast.error(getErrorMessage(err));
          setTimeout(() => resetModal(), 100);
        },
      }
    );
  };

  const hasValueChanged = () => (modalData.value?.trim() || "") !== (modalData.originalValue?.trim() || "");

  const handleUpdate = () => {
    if (!activeLang) return toast.error(strings.pleaseSelectLanguage);

    const trimmedValue = modalData.value?.trim();
if (!trimmedValue) {
  toast.error(strings.valueisrequired);
  resetModal();
  return;
}

if (!hasValueChanged()) {
  toast.info(strings.noChangesDetected);
  resetModal();
  return;
}


    mutate(
      { url: `${UPDATE_STATIC_STRINGS}?id=${modalData.id}`, method: "PUT", data: { value: trimmedValue } },
      {
        onSuccess: (res) => {
          toast.success(res.message);
          resetModal();
          invalidateAndRefetch();
        },
        onError: (err) => {
          toast.error(getErrorMessage(err));
          resetModal();
        },
      }
    );
  };

  const handleSave = () => {
    if (!activeLang?.trim()) return toast.error(strings.pleaseSelectLanguage);

    if (modalType === "add" && addMode === "single") handleAddSingle();
    else if (modalType === "add" && addMode === "json") handleAddJSON();
    else if (modalType === "edit") handleUpdate();
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (!deleteId) return toast.error(strings.noitemfordeletion);
    if (!activeLang) return toast.error(strings.pleaseSelectLanguage);

    mutate(
      { url: `${DELETE_STATIC_STRINGS}?id=${deleteId}`, method: "DELETE" },
      {
        onSuccess: (res) => {
          toast.success(res.message);
          setShowDeleteModal(false);
          setDeleteId(null);
          invalidateAndRefetch();
        },
        onError: (err) => toast.error(getErrorMessage(err)),
      }
    );
  };

  const handleCancelDelete = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

  const handleLanguageChange = (lang) => {
    if (lang && lang !== activeLang) {
      setActiveLang(lang);
      if (showModal) resetModal();
    }
  };

  const renderPageHeader = (showActionButton = false) => (
    <>
      <PageHeader title={strings.staticStrings} description={strings.staticStringsDescription} className="mb-6">
        {showActionButton && (
          <AddButton
            onOptionSelect={handleOptionSelect}
            showOptions={showAddOptionsHeader}
            setShowOptions={setShowAddOptionsHeader}
            variant="header"
          />
        )}
      </PageHeader>
      <div className="mb-6 flex justify-between" padding={true}>
        <LanguageTabs
          activeLanguage={activeLang}
          onLanguageChange={handleLanguageChange}
          variant="buttons"
          useApi={true}
          autoSelectFirst={true}
        />
        <span className="text-secondary font-semibold">
          {strings.totalStrings} {data?.results?.totalCount}
        </span>
      </div>
    </>
  );

  if (!activeLang)
    return (
      <div className="max-w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto ">
          {renderPageHeader(false)}
          <Card>
            <EmptyState
              message={strings.selectLanguageToView}
              description={strings.noLanguagesAvailable}
            />
          </Card>
        </div>
      </div>
    );

  if (isListLoading)
    return (
      <div className="max-w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto ">
          {renderPageHeader(false)}
          <Card>
            <Loader />
          </Card>
        </div>
      </div>
    );

  if (isListError)
    return (
      <div className="max-w-full overflow-x-hidden">
        <div className="max-w-7xl mx-auto ">
          {renderPageHeader(false)}
          <Card>
            <EmptyState
              message={strings.failedToLoadStaticStrings}
              description={listError?.message}
            />
          </Card>
        </div>
      </div>
    );

  return (
    <div className="max-w-full overflow-x-hidden">
      <div className="max-w-7xl mx-auto ">
        {renderPageHeader(true)}
        <div className="bg-white p-4 sm:p-6 rounded-2xl">
          <StaticStringsTable
            data={data}
            activeLang={activeLang}
            onEditClick={handleUpdateClick}
            onDeleteClick={handleDeleteClick}
            onAddOptionSelect={handleOptionSelect}
            showAddOptions={showAddOptions}
            setShowAddOptions={setShowAddOptions}
          />
        </div>
      </div>

      <StaticStringsModal
        isOpen={showModal}
        onClose={resetModal}
        modalType={modalType}
        addMode={addMode}
        activeLang={activeLang}
        modalData={modalData}
        setModalData={setModalData}
        keyError={keyError}
        setKeyError={setKeyError}
        onSave={handleSave}
        isMutating={isMutating}
        hasValueChanged={hasValueChanged}
      />

      {showDeleteModal && (
        <DeletModel 
          handleConfirmDelete={handleConfirmDelete} 
          handleCancelDelete={handleCancelDelete}
          isLoading={isMutating}
        />
      )}
    </div>
  );
};

export default StaticStrings;
